import { forwardRef, memo, useCallback, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import { Close as CloseIcon } from '@material-ui/icons';
import { getLogger } from '../../utilities/UseLog';
import useDialogStyles from './UseDialogStyles';

const log = getLogger('BaseDialog');

/**
 * Base dialog component providing common dialog behavior and structure
 *
 * Handles:
 * - Open/close state management
 * - Loading state during async actions
 * - Error handling for callbacks
 * - Backdrop click behavior
 * - Close button in title bar
 * - Keyboard navigation (ESC, tabIndex)
 *
 * Exposes via ref:
 * - handleOnClose: Function to close dialog with full cleanup logic
 *
 * @component
 * @example
 * const dialogRef = useRef();
 * <BaseDialog
 *   ref={dialogRef}
 *   title="Confirmation"
 *   open={open}
 *   setOpen={setOpen}
 *   onClose={handleClose}
 *   disableBackdropClick
 *   actions={<Button onClick={() => dialogRef.current.handleOnClose()}>Cancel</Button>}
 * >
 *   <div>Are you sure?</div>
 * </BaseDialog>
 */
const BaseDialog = forwardRef((props, ref) => {
    const {
        title,
        children,
        open,
        setOpen,
        onCloseFunc,
        actions,
        disableBackdropClick = false,
        closeInTitleBar = true,
        maxWidth = 'md',
        fullWidth = false,
        paperProps,
        isActioning: externalIsActioning,
        onActioningChange,
        dialogId = 'base-dialog',
    } = props;

    const classes = useDialogStyles();
    const [internalIsActioning, setInternalIsActioning] = useState(false);

    // Use external state if provided, otherwise use internal state
    const isActioning = externalIsActioning !== undefined ? externalIsActioning : internalIsActioning;
    const setIsActioning = onActioningChange || setInternalIsActioning;

    /**
     * Handles the close event
     * Safely executes optional close callback with error handling
     */
    const handleClose = useCallback(() => {
        if (typeof onCloseFunc === 'function') {
            try {
                onCloseFunc();
            } catch (error) {
                log.error('Error in onCloseFunc:', error);
            }
        }
    }, [onCloseFunc]);

    /**
     * Handles dialog close event (backdrop click, ESC key, close button)
     */
    const handleOnClose = useCallback(
        (event, reason) => {
            // Prevent closing on backdrop click if disabled
            if (reason === 'backdropClick' && disableBackdropClick) {
                return;
            }

            setIsActioning(false);
            handleClose();
            setOpen(false);
        },
        [disableBackdropClick, handleClose, setOpen, setIsActioning]
    );

    /**
     * Expose handleOnClose method to parent components via ref
     */
    useImperativeHandle(
        ref,
        () => ({
            handleOnClose,
        }),
        [handleOnClose]
    );

    return (
        <Dialog
            open={open}
            onClose={handleOnClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            PaperProps={paperProps}
            aria-labelledby={`${dialogId}-title`}
        >
            <DialogTitle id={`${dialogId}-title`}>
                {title}
                {closeInTitleBar && (
                    <IconButton
                        aria-label="cancel"
                        className={classes.closeButton}
                        onClick={handleOnClose}
                        tabIndex={-1}
                        disabled={isActioning}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent className="flex-container">{children}</DialogContent>
            {actions && <DialogActions>{actions}</DialogActions>}
        </Dialog>
    );
});

BaseDialog.displayName = 'BaseDialog';

BaseDialog.propTypes = {
    /** Dialog title */
    title: PropTypes.node.isRequired,
    /** Dialog content */
    children: PropTypes.node,
    /** Controls whether the dialog is open */
    open: PropTypes.bool.isRequired,
    /** Function to set the open state */
    setOpen: PropTypes.func.isRequired,
    /** Optional callback when dialog closes */
    onCloseFunc: PropTypes.func,
    /** Action buttons to render in DialogActions */
    actions: PropTypes.node,
    /** If true, prevents closing on backdrop click */
    disableBackdropClick: PropTypes.bool,
    /** Shows/hides close button in title bar */
    closeInTitleBar: PropTypes.bool,
    /** Maximum width of the dialog */
    maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
    /** If true, dialog stretches to maxWidth */
    fullWidth: PropTypes.bool,
    /** Props applied to the Paper element */
    paperProps: PropTypes.object,
    /** External control of actioning state */
    isActioning: PropTypes.bool,
    /** Callback to update external actioning state */
    onActioningChange: PropTypes.func,
    /** Unique ID for dialog accessibility */
    dialogId: PropTypes.string,
};

BaseDialog.defaultProps = {
    children: null,
    onCloseFunc: null,
    actions: null,
    disableBackdropClick: false,
    closeInTitleBar: true,
    maxWidth: 'md',
    fullWidth: false,
    paperProps: undefined,
    isActioning: undefined,
    onActioningChange: null,
    dialogId: 'base-dialog',
};

export default memo(BaseDialog);
